import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import _isEqual from 'lodash/isEqual';

import ControlPanel from './image/ControlPanel';
import useDomLocationInWysiwygContainer from '../../../hooks/wysiwyg_editor/useDomLocationInWysiwygContainer';
import DragFrame from '@src/components/wysiwyg_editor/page_content_items/DragFrame';
import InnerDragHandle from '@src/components/wysiwyg_editor/page_content_items/InnerDragHandle';
import _clamp from 'lodash/clamp';
import { handleElementMovement, MINIMUM_DIMENSION, SNAP_DISTANCE } from '@src/utils/wysiwyg_editor/elementMovement';
import { HANDLE_POSITIONS } from '@src/components/wysiwyg_editor/page_content_items/DragHandle';
import useLocationInViewbox from '@src/hooks/wysiwyg_editor/useLocationInViewbox';
import { handleElementScaling } from '@src/utils/wysiwyg_editor/elementScaling';
import DisableUserSelect from '@src/components/DisableUserSelect';
import ForceCursor from '@src/components/ForceCursor';
import ContentDragShield from '@src/components/wysiwyg_editor/page_content_items/ContentDragShield';
import { Portal } from '@src/components/Portal';
import useOnClickOutside from 'use-onclickoutside';
import type { TRect, TSize } from '../../../types/misc';
import { calculateLocations } from '../../../utils/wysiwyg_editor/contentItemHandleLocations';
import useSnappableLocations from '../../../hooks/wysiwyg_editor/useSnappableLocations';
import SnapLines from '../SnapLines';
import { WysiwygEditorDispatchContext, WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import usePageContentItemStackOrder from '@src/hooks/wysiwyg_editor/usePageContentItemStackOrder';
import {
  clearImage,
  saveSnapshot,
  updateContentItemContainerLocation,
  updateContentItemContentLocation,
  updateContentItemLocations,
  updateImageContentItemZoom
} from '@src/actions/wysiwyg/editor';
import { useDispatch } from 'react-redux';
import {
  constrainedImageLocation,
  convertSizeToRenderPercentages,
  convertSizeToViewboxPercentages,
  getMinimumDimensionsFromImageDimensionsToCoverArea,
  getMinimumDimensionsFromNaturalDimensionsToCoverArea
} from '@src/utils/wysiwyg_editor/imagePositioning';
import ClippedImage from './image/ClippedBuilderImage';
import type { Props } from './Image';
import useIsLargeScreen from '@src/hooks/useIsLargeScreen';
import { ImageCacheDispatchContext } from '@src/contexts/wysiwyg/image_cache';
import ImageCallToAction from './ImageCallToAction';
import useNoRenderRef from '@src/hooks/useNoRenderRef';
import useSetImageContent from '@src/hooks/wysiwyg_editor/useSetImageContent';
import { PageContentDispatchContext } from '@src/contexts/wysiwyg/page_content';
import KeyBindingDelete from '../key_bindings/delete';
import usePreviousUnique from '@src/hooks/usePreviousUnique';
import _pick from 'lodash/pick';

export const DISPLAY_STATES = {
  hover: 'HOVER',
  render: 'RENDER',
  move: 'MOVE',
  moving: 'MOVING',
  cropping: 'CROPPING',
  scaling: 'SCALING',
  movingClippedImage: 'MOVING_CLIPPED_IMAGE'
};

const FOCUSED_STATES = Object.values(
  _pick(DISPLAY_STATES, ['move', 'moving', 'cropping', 'scaling', 'movingClippedImage'])
);

const STACK_ORDER_GROUPINGS = {
  image: 1,
  handles: 100,
  activeHandles: 200,
  controlPanel: 300
};

function locationHasNan(location: TRect | Record<string, never>) {
  return (
    Number.isNaN(location.x) ||
    Number.isNaN(location.y) ||
    Number.isNaN(location.height) ||
    Number.isNaN(location.width)
  );
}

function BuilderImage({
  data,
  frameDomSpringValues,
  frameDomSpringApi,
  frameViewboxSpringValues,
  frameViewboxSpringApi
}: Props): React.ReactElement {
  const isLarge = useIsLargeScreen();
  const dispatch = useDispatch();
  const {
    renderableArea: renderableViewboxArea,
    renderableDomLocation,
    renderRect: stencilRenderRect,
    svgRef,
    viewbox
  } = useContext(WysiwygEditorStateContext);
  const { setInsertionTarget } = useContext(ImageCacheDispatchContext);
  const { openToolboxTab } = useContext(WysiwygEditorDispatchContext);
  const { setSelectedPageContentItemId } = useContext(PageContentDispatchContext);

  const [isBeingDragged, setIsBeingDragged] = useState(false);
  const [displayState, setDisplayState] = useState(() => {
    return DISPLAY_STATES.render;
  });
  const clipLocationInDom = useDomLocationInWysiwygContainer(data.containerLocation);
  const clipLocationInViewbox = useLocationInViewbox(data.containerLocation);
  const imageLocationInViewbox = useLocationInViewbox(data.contentLocation);
  const [minimumDimensions, setMinimumDimensions] = useState<TSize | null | undefined>(() => {
    return imageLocationInViewbox
      ? convertSizeToViewboxPercentages(
          getMinimumDimensionsFromImageDimensionsToCoverArea(imageLocationInViewbox, clipLocationInViewbox),
          viewbox
        )
      : null;
  });
  const stackOrder = usePageContentItemStackOrder(data.id);
  const snappableLocations = useSnappableLocations(data.id);

  const initialFrameDragLocationRef = useRef<TRect | null | undefined>(null);
  const isDraggingZoomSliderRef = useRef<boolean>(false);
  const initialContentDragLocationRef = useRef<TRect | Record<string, never>>(null);
  const initialSnappableLocationsRef = useRef(snappableLocations);
  const domElementsRef = useRef<HTMLDivElement | null | undefined>(null);
  const initialZoomRef = useRef<number | null>(null);
  const initialDisplayStateRef = useRef<string>(null);

  const previousDisplayState = usePreviousUnique(displayState);

  const [imageSpringValues, imageSpringApi] = useSpring<TRect>(
    () => ({
      config: { duration: 1 },
      ...imageLocationInViewbox
    }),
    [imageLocationInViewbox]
  );

  const [_zoomSpringValue, zoomSpringApi] = useSpring<{ zoom: number }>(
    () => ({
      config: { duration: 1 },
      zoom: data.zoom
    }),
    [data.zoom]
  );

  const noRenderClipLocationInDom = useNoRenderRef(clipLocationInDom);
  const noRenderStencilRenderRect = useNoRenderRef(stencilRenderRect);

  const renderPointRef = useRef<HTMLDivElement | null | undefined>(null);

  const handleImageChange = useCallback(() => {
    dispatch(saveSnapshot());
    dispatch(clearImage(data.id));
  }, [data.id, dispatch]);

  const handleZoomDragStart = useCallback(() => {
    isDraggingZoomSliderRef.current = true;
    initialFrameDragLocationRef.current = data.containerLocation;
    initialContentDragLocationRef.current = data.contentLocation.height ? data.contentLocation : null;
  }, [data.containerLocation, data.contentLocation]);

  const handleZoomChange = useCallback(
    (newZoomLevel: number, isDone: boolean) => {
      if (typeof minimumDimensions?.height !== 'number') return;
      if (!initialContentDragLocationRef.current) return;
      if (!initialFrameDragLocationRef.current) return;

      // If a user clicks and releases on the slider instead of dragging it,
      // the start handler doesn't get fired so the ref will be false. If that
      // is the case, we need to create an undo snapshot.
      if (isDone && !isDraggingZoomSliderRef.current) {
        dispatch(saveSnapshot());
      }

      const initialContentLocation = initialContentDragLocationRef.current;
      const initialFrameLocation = initialFrameDragLocationRef.current;

      const halfClipHeight = initialFrameLocation.height / 2;
      const halfClipWidth = initialFrameLocation.width / 2;

      const imageDistanceXOutsideOfClipArea = initialContentLocation.x * -1;
      const imageDistanceYOutsideOfClipArea = initialContentLocation.y * -1;

      // location where image intersects with center of clip area
      const imageIntersectionWithClipXCenter = imageDistanceXOutsideOfClipArea + halfClipWidth;
      const imageIntersectionWithClipYCenter = imageDistanceYOutsideOfClipArea + halfClipHeight;

      // converted to percentage relative to image (180px tall image is 180%
      // of a 100px tall viewbox, so we are converting to a percentage based on
      // 180px instead of 100px)
      const centerPointXOnImage = imageIntersectionWithClipXCenter / initialContentLocation.width;
      const centerPointYOnImage = imageIntersectionWithClipYCenter / initialContentLocation.height;

      const newDimensions = {
        height: newZoomLevel * minimumDimensions.height,
        width: newZoomLevel * minimumDimensions.width
      };

      const newPosition = {
        x: halfClipWidth - newDimensions.width * centerPointXOnImage,
        y: halfClipHeight - newDimensions.height * centerPointYOnImage
      };

      const newLocation = constrainedImageLocation(initialFrameLocation, {
        x: newPosition.x,
        y: newPosition.y,
        height: newDimensions.height,
        width: newDimensions.width
      });

      if (isDone) {
        dispatch(updateContentItemContentLocation(data.id, newLocation));
        dispatch(updateImageContentItemZoom(data.id, newZoomLevel));
      } else {
        const newLocationInViewboxPixels = {
          x: newLocation.x * viewbox.width,
          y: newLocation.y * viewbox.height,
          height: newLocation.height * viewbox.height,
          width: newLocation.width * viewbox.width
        };

        imageSpringApi(newLocationInViewboxPixels);
        zoomSpringApi({ zoom: newZoomLevel });
      }
    },
    [
      data.id,
      dispatch,
      imageSpringApi,
      minimumDimensions.height,
      minimumDimensions.width,
      viewbox.height,
      viewbox.width,
      zoomSpringApi
    ]
  );

  useEffect(() => {
    const handleBlur = () => setDisplayState(DISPLAY_STATES.render);
    const svg = svgRef.current;

    svg.addEventListener('blur-content-items', handleBlur);

    return () => {
      svg.removeEventListener('blur-content-items', handleBlur);
    };
  }, [svgRef]);

  // when the image's natural dimensions change (i.e. the image was changed),
  // we need to update the minimum dimensions so that zooming works properly
  useEffect(() => {
    if (data.naturalDimensions && noRenderClipLocationInDom.current) {
      const minimumDimensions = getMinimumDimensionsFromNaturalDimensionsToCoverArea(
        noRenderClipLocationInDom.current,
        data.naturalDimensions
      );

      const dimensionsAsPercentages = convertSizeToRenderPercentages(
        minimumDimensions,
        noRenderStencilRenderRect.current
      );

      setMinimumDimensions(dimensionsAsPercentages);
    }
  }, [data.naturalDimensions, noRenderClipLocationInDom, noRenderStencilRenderRect]);

  useEffect(() => {
    if (FOCUSED_STATES.includes(displayState) && !FOCUSED_STATES.includes(previousDisplayState)) {
      setSelectedPageContentItemId(data.id);
    }
  }, [displayState, data.id, setSelectedPageContentItemId, previousDisplayState]);

  const handleImageDrag = useCallback(
    (_position: string, movementInPixels: [number, number], _isFirst: boolean, isLast: boolean) => {
      if (!stencilRenderRect) return;
      if (!initialContentDragLocationRef.current) return;
      if (!initialFrameDragLocationRef.current) return;

      const initialLocation = initialContentDragLocationRef.current;

      const movementPercentages = [
        movementInPixels[0] / stencilRenderRect.width,
        movementInPixels[1] / stencilRenderRect.height
      ];

      const newLocation = constrainedImageLocation(initialFrameDragLocationRef.current, {
        ...initialLocation,
        x: initialLocation.x + movementPercentages[0],
        y: initialLocation.y + movementPercentages[1]
      });

      if (isLast) {
        if (!_isEqual(initialLocation, newLocation)) {
          dispatch(saveSnapshot());
        }
        dispatch(updateContentItemContentLocation(data.id, newLocation));
      } else {
        const newLocationInViewboxPixels = {
          x: newLocation.x * viewbox.width,
          y: newLocation.y * viewbox.height,
          height: newLocation.height * viewbox.height,
          width: newLocation.width * viewbox.width
        };

        imageSpringApi(newLocationInViewboxPixels);
      }
    },
    [data.id, dispatch, imageSpringApi, stencilRenderRect, viewbox.height, viewbox.width]
  );

  // Not necessary anymore since all motions are handled by spring rather than
  // redux. This should be phased out eventually.
  const handleImageDragStart = useCallback(() => {
    if (!data.contentLocation.height) return;

    initialFrameDragLocationRef.current = data.containerLocation;
    initialContentDragLocationRef.current = data.contentLocation;
    initialSnappableLocationsRef.current = snappableLocations;
    setDisplayState(DISPLAY_STATES.movingClippedImage);
  }, [data.containerLocation, data.contentLocation, snappableLocations]);

  const handleImageDragStop = useCallback(() => {
    setDisplayState(DISPLAY_STATES.move);
  }, []);

  // Not necessary anymore since all motions are handled by spring rather than
  // redux. This should be phased out eventually.
  const handleFrameDragStart = useCallback(
    (position) => {
      initialDisplayStateRef.current = displayState;
      initialFrameDragLocationRef.current = data.containerLocation;
      initialContentDragLocationRef.current = data.contentLocation.height ? data.contentLocation : null;
      initialSnappableLocationsRef.current = snappableLocations;
      initialZoomRef.current = data.zoom;

      if ([HANDLE_POSITIONS.n, HANDLE_POSITIONS.e, HANDLE_POSITIONS.s, HANDLE_POSITIONS.w].includes(position)) {
        setDisplayState(DISPLAY_STATES.cropping);
      }

      if ([HANDLE_POSITIONS.ne, HANDLE_POSITIONS.nw, HANDLE_POSITIONS.se, HANDLE_POSITIONS.sw].includes(position)) {
        setDisplayState(DISPLAY_STATES.scaling);
      }

      if (position === HANDLE_POSITIONS.inner) {
        setDisplayState(DISPLAY_STATES.moving);
      }
      setIsBeingDragged(true);
    },
    [data.containerLocation, data.contentLocation, data.zoom, displayState, snappableLocations]
  );

  const handleFirstEmptyFrameClick = useCallback(() => {
    if (isLarge && !data.href) {
      openToolboxTab('photos');
    }
  }, [data, isLarge, openToolboxTab]);

  const handleSubsequentEmptyFrameClick = useCallback(() => {
    if (!data.href) {
      openToolboxTab('photos');
      setInsertionTarget(data.id);
    }
  }, [data.id, data.href, setInsertionTarget, openToolboxTab]);

  const handleClick = useCallback(() => {
    if ([DISPLAY_STATES.render, DISPLAY_STATES.hover].includes(initialDisplayStateRef.current)) {
      handleFirstEmptyFrameClick();
    }

    if (initialDisplayStateRef.current === DISPLAY_STATES.move) {
      handleSubsequentEmptyFrameClick();
    }
  }, [handleFirstEmptyFrameClick, handleSubsequentEmptyFrameClick]);

  const handleFrameDragStop = useCallback(() => {
    setDisplayState(DISPLAY_STATES.move);
    setIsBeingDragged(false);
  }, []);

  const handleFrameWithImageDrag = useCallback(
    (position: string, movementInPixels: [number, number], _isFirst: boolean, isLast: boolean) => {
      if (!initialFrameDragLocationRef.current) return;
      if (!initialContentDragLocationRef.current) return;
      if (!initialContentDragLocationRef.current) return;
      if (initialZoomRef.current === null) return;
      if (!renderableDomLocation) return;
      if (!renderableViewboxArea) return;
      if (!stencilRenderRect) return;

      const movementPercentages = [
        movementInPixels[0] / stencilRenderRect.width,
        movementInPixels[1] / stencilRenderRect.height
      ];
      const initialContentDragLocation = {
        ...initialContentDragLocationRef.current
      };
      const initialFrameDragLocation = {
        ...initialFrameDragLocationRef.current
      };
      let newContainerLocation;
      let newContentLocation;

      if (
        [
          HANDLE_POSITIONS.n,
          HANDLE_POSITIONS.e,
          HANDLE_POSITIONS.s,
          HANDLE_POSITIONS.w,
          HANDLE_POSITIONS.inner
        ].includes(position)
      ) {
        newContainerLocation = handleElementMovement(
          position,
          initialFrameDragLocation,
          movementPercentages,
          renderableViewboxArea,
          {
            height: MINIMUM_DIMENSION / stencilRenderRect.height,
            width: MINIMUM_DIMENSION / stencilRenderRect.width
          },
          initialSnappableLocationsRef.current,
          {
            x: SNAP_DISTANCE / stencilRenderRect.width,
            y: SNAP_DISTANCE / stencilRenderRect.height
          }
        );

        newContentLocation = initialContentDragLocation;
      } else {
        const scaled = handleElementScaling(
          position,
          initialFrameDragLocation,

          initialContentDragLocation,
          movementPercentages,
          renderableViewboxArea,
          {
            height: MINIMUM_DIMENSION / stencilRenderRect.height,
            width: MINIMUM_DIMENSION / stencilRenderRect.width
          }
        );

        newContainerLocation = scaled.frame;
        newContentLocation = scaled.content;
      }

      if (
        [HANDLE_POSITIONS.n, HANDLE_POSITIONS.e, HANDLE_POSITIONS.s, HANDLE_POSITIONS.w].includes(position) &&
        initialContentDragLocation
      ) {
        const containerLocationDiff = {
          height: newContainerLocation.height - initialFrameDragLocation.height,
          width: newContainerLocation.width - initialFrameDragLocation.width,
          x: newContainerLocation.x - initialFrameDragLocation.x,
          y: newContainerLocation.y - initialFrameDragLocation.y
        };

        const imageHeightWidthRatio = initialContentDragLocation.height / initialContentDragLocation.width;

        if (newContentLocation.width < newContainerLocation.width) {
          newContentLocation.width = newContainerLocation.width;
          newContentLocation.height = newContainerLocation.width * imageHeightWidthRatio;
        }

        if (newContentLocation.height < newContainerLocation.height) {
          newContentLocation.height = newContainerLocation.height;
          newContentLocation.width = newContainerLocation.height / imageHeightWidthRatio;
        }

        newContentLocation.x = _clamp(
          newContentLocation.x - containerLocationDiff.x,
          newContainerLocation.width - newContentLocation.width,
          0
        );

        newContentLocation.y = _clamp(
          newContentLocation.y - containerLocationDiff.y,
          newContainerLocation.height - newContentLocation.height,
          0
        );
      }

      if (isLast) {
        // If neither the container nor the content moved, we don't want to
        // update the state. That is becuase the zoom changes from 1 to 1.0011
        // sometimes when the division comes out that way. This messes up the
        // next undo save point.
        if (
          !_isEqual(initialFrameDragLocation, newContainerLocation) ||
          !_isEqual(initialContentDragLocation, newContentLocation)
        ) {
          dispatch(saveSnapshot());
          dispatch(updateContentItemLocations(data.id, newContainerLocation, newContentLocation));

          const newMinDimensions = getMinimumDimensionsFromImageDimensionsToCoverArea(
            newContentLocation,
            newContainerLocation
          );

          setMinimumDimensions(newMinDimensions);

          const newZoom = Math.min(
            newContentLocation.width / newMinDimensions.width,
            newContentLocation.height / newMinDimensions.height
          );

          dispatch(updateImageContentItemZoom(data.id, newZoom));
        }
      } else {
        const newContentLocationInPixels = {
          x: newContainerLocation.x * stencilRenderRect.width,
          y: newContainerLocation.y * stencilRenderRect.height,
          height: newContainerLocation.height * stencilRenderRect.height,
          width: newContainerLocation.width * stencilRenderRect.width
        };
        frameDomSpringApi(calculateLocations(newContentLocationInPixels));

        const newContainerLocationInViewboxPixels = {
          x: newContainerLocation.x * viewbox.width,
          y: newContainerLocation.y * viewbox.height,
          height: newContainerLocation.height * viewbox.height,
          width: newContainerLocation.width * viewbox.width
        };
        frameViewboxSpringApi(newContainerLocationInViewboxPixels);

        const newMinDimensions = getMinimumDimensionsFromImageDimensionsToCoverArea(
          newContentLocation,
          newContainerLocation
        );

        const newZoom = Math.min(
          newContentLocation.width / newMinDimensions.width,
          newContentLocation.height / newMinDimensions.height
        );

        const newImageLocationInViewbox = {
          x: newContentLocation.x * viewbox.width,
          y: newContentLocation.y * viewbox.height,
          height: newContentLocation.height * viewbox.height,
          width: newContentLocation.width * viewbox.width
        };

        imageSpringApi(newImageLocationInViewbox);
        zoomSpringApi({ zoom: newZoom });
      }
    },
    [
      renderableDomLocation,
      renderableViewboxArea,
      stencilRenderRect,
      dispatch,
      data.id,
      frameDomSpringApi,
      viewbox.width,
      viewbox.height,
      frameViewboxSpringApi,
      imageSpringApi,
      zoomSpringApi
    ]
  );

  const handleEmptyFrameDrag = useCallback(
    (position: string, movementInPixels: [number, number], _isFirst: boolean, isLast: boolean) => {
      if (!initialFrameDragLocationRef.current) return;
      if (!renderableDomLocation) return;
      if (!renderableViewboxArea) return;
      if (!stencilRenderRect) return;

      const movementPercentages = [
        movementInPixels[0] / stencilRenderRect.width,
        movementInPixels[1] / stencilRenderRect.height
      ];
      const initialFrameDragLocation = {
        ...initialFrameDragLocationRef.current
      };
      let newContainerLocation;

      if (
        [
          HANDLE_POSITIONS.n,
          HANDLE_POSITIONS.e,
          HANDLE_POSITIONS.s,
          HANDLE_POSITIONS.w,
          HANDLE_POSITIONS.inner
        ].includes(position)
      ) {
        newContainerLocation = handleElementMovement(
          position,
          initialFrameDragLocation,
          movementPercentages,
          renderableViewboxArea,
          {
            height: MINIMUM_DIMENSION / stencilRenderRect.height,
            width: MINIMUM_DIMENSION / stencilRenderRect.width
          },
          initialSnappableLocationsRef.current,
          {
            x: SNAP_DISTANCE / stencilRenderRect.width,
            y: SNAP_DISTANCE / stencilRenderRect.height
          }
        );
      } else {
        const scaled = handleElementScaling(
          position,
          initialFrameDragLocation,
          { height: 0, width: 0, x: 0, y: 0 },
          movementPercentages,
          renderableViewboxArea,
          {
            height: MINIMUM_DIMENSION / stencilRenderRect.height,
            width: MINIMUM_DIMENSION / stencilRenderRect.width
          }
        );

        newContainerLocation = scaled.frame;
      }

      if (isLast) {
        if (!_isEqual(initialFrameDragLocation, newContainerLocation)) {
          dispatch(saveSnapshot());
        }

        dispatch(updateContentItemContainerLocation(data.id, newContainerLocation));
      } else {
        const locationInViewboxPixels = {
          x: newContainerLocation.x * viewbox.width,
          y: newContainerLocation.y * viewbox.height,
          height: newContainerLocation.height * viewbox.height,
          width: newContainerLocation.width * viewbox.width
        };

        const locationInDomPixels = {
          x: newContainerLocation.x * stencilRenderRect.width,
          y: newContainerLocation.y * stencilRenderRect.height,
          height: newContainerLocation.height * stencilRenderRect.height,
          width: newContainerLocation.width * stencilRenderRect.width
        };

        frameViewboxSpringApi(locationInViewboxPixels);
        frameDomSpringApi(calculateLocations(locationInDomPixels));
      }
    },
    [
      data.id,
      dispatch,
      frameDomSpringApi,
      frameViewboxSpringApi,
      renderableDomLocation,
      renderableViewboxArea,
      stencilRenderRect,
      viewbox.height,
      viewbox.width
    ]
  );

  const handleHoverStart = useCallback(() => {
    if (displayState === DISPLAY_STATES.render) {
      setDisplayState(DISPLAY_STATES.hover);
    }
  }, [displayState]);

  const handleHoverEnd = useCallback(() => {
    if (displayState === DISPLAY_STATES.hover) {
      setDisplayState(DISPLAY_STATES.render);
    }
  }, [displayState]);

  useOnClickOutside(domElementsRef, () => {
    setDisplayState(DISPLAY_STATES.render);
  });

  const handlesAreActive = displayState !== DISPLAY_STATES.render && displayState !== DISPLAY_STATES.hover;

  return (
    <Fragment>
      {displayState === DISPLAY_STATES.movingClippedImage ? (
        <Fragment>
          <DisableUserSelect />
          <ForceCursor cursor="grabbing" />
        </Fragment>
      ) : null}

      {displayState === DISPLAY_STATES.move ? <KeyBindingDelete itemId={data.id} /> : null}

      <Portal htmlId="wysiwyg-editor-portal" ref={domElementsRef}>
        {data.href &&
        imageLocationInViewbox &&
        !locationHasNan(imageLocationInViewbox) &&
        data.contentLocation.height ? (
          <ClippedImage
            disableClip={displayState === DISPLAY_STATES.movingClippedImage}
            imageUrl={data.href}
            frameSpringValues={frameViewboxSpringValues}
            imageSpringValues={imageSpringValues}
            stackOrder={stackOrder + STACK_ORDER_GROUPINGS.image}
            key="image"
          />
        ) : null}

        {clipLocationInDom && (
          <animated.div
            ref={renderPointRef}
            style={{
              height: 0,
              width: 0,
              position: 'absolute',
              top: frameDomSpringValues.y,
              left: frameDomSpringValues.x
            }}
          />
        )}

        {displayState === DISPLAY_STATES.move && clipLocationInDom ? (
          <ControlPanel
            anchorLocation={clipLocationInDom}
            anchorRef={renderPointRef}
            zoomLevel={data.zoom}
            imageData={data}
            stackOrder={stackOrder + STACK_ORDER_GROUPINGS.controlPanel}
            onImageChange={handleImageChange}
            onZoomChange={handleZoomChange}
            onZoomDragStart={handleZoomDragStart}
            key={`control-panel-${data.href || 'no-image'}`}
          />
        ) : null}

        {!data.href && clipLocationInDom ? (
          <ImageCallToAction
            currentDisplayState={displayState}
            frameDomSpringValues={frameDomSpringValues}
            stackOrder={stackOrder + STACK_ORDER_GROUPINGS.image}
            onMouseEnter={handleHoverStart}
            onMouseLeave={handleHoverEnd}
          />
        ) : null}

        {clipLocationInDom ? (
          <ContentDragShield
            location={frameDomSpringValues}
            pageItemId={data.id}
            zIndex={stackOrder + STACK_ORDER_GROUPINGS.handles}
            onClick={handleClick}
            onDrag={data.href ? handleFrameWithImageDrag : handleEmptyFrameDrag}
            onDragStart={handleFrameDragStart}
            onDragStop={handleFrameDragStop}
            onPointerEnter={handleHoverStart}
            onPointerLeave={handleHoverEnd}
          />
        ) : null}

        {clipLocationInDom && displayState !== DISPLAY_STATES.render ? (
          <DragFrame
            locations={frameDomSpringValues}
            pageItemId={data.id}
            zIndex={
              stackOrder + (handlesAreActive ? STACK_ORDER_GROUPINGS.activeHandles : STACK_ORDER_GROUPINGS.handles)
            }
            onDrag={data.href ? handleFrameWithImageDrag : handleEmptyFrameDrag}
            onDragStart={handleFrameDragStart}
            onDragStop={handleFrameDragStop}
            noHandles={displayState === DISPLAY_STATES.hover}
          />
        ) : null}

        {clipLocationInDom &&
        data.href &&
        [DISPLAY_STATES.move, DISPLAY_STATES.movingClippedImage].includes(displayState) ? (
          <InnerDragHandle
            cursor="grab"
            forceCursor={false}
            location={frameDomSpringValues}
            pageItemId={data.id}
            zIndex={
              stackOrder + (handlesAreActive ? STACK_ORDER_GROUPINGS.activeHandles : STACK_ORDER_GROUPINGS.handles)
            }
            onDragStart={handleImageDragStart}
            onDragStop={handleImageDragStop}
            onDrag={handleImageDrag}
          />
        ) : null}
        {isBeingDragged && clipLocationInDom && snappableLocations ? (
          <SnapLines
            contentItem={data}
            frameLocation={frameViewboxSpringValues}
            snappableLocations={snappableLocations}
          />
        ) : null}
      </Portal>
    </Fragment>
  );
}

export default BuilderImage;
